import AlertService from "../../../../services/AlertService";
import PracticalExamService from "../../../../services/PracticalExamService";
import SharedService from "../../../../services/SharedService";
import AuthContext from "../../../../auth/AuthContext";
import { Fragment, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PracticalExamQuestion from "./PracticalQuestion";
import { useTimer } from "react-timer-hook";
import ChronometerIcon from '../../../../assets/icons/ChronoMeter.svg';
import QuestionMarkIcon from '../../../../assets/icons/QuestionMark.svg';
import PreviousIcon from '../../../../assets/icons/Previous.svg';
import WhitePreviousIcon from '../../../../assets/icons/Previous.svg';
import NextIcon from '../../../../assets/icons/Next.svg';
import WhiteNextIcon from '../../../../assets/icons/Next.svg';
import { Button } from "react-bootstrap";
import { Skeleton } from "primereact/skeleton";
import ConfirmDialogBox from "../../../confirmDialog/ConfirmDialogBox";
import { ThemeContext } from "../../../../theme/ThemeContext";


const PracticalExam = () => {
    const navigate = useNavigate();
    const { assessmentName } = useParams();
    const { state } = useLocation();
    const [loading, setLoading] = useState(false);
    const [assessmentExam, setAssessmentExam] = useState();
    const [questions, setQuestions] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState();
    const [timerText, setTimerText] = useState('00:00:00');
    const authContext = useContext(AuthContext);
    const [selectedCardId, setSelectedCardId] = useState(0);
    const [selectedCard, setSelectedCard] = useState();
    const scrollRef = useRef();
    const [isExpired, setIsExpired] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";
    const submitControllerRef = useRef(null);


    const {
        totalSeconds,
        seconds,
        minutes,
        hours,
        restart,
    } = useTimer({
        expiryTimestamp: new Date(),
        autoStart: false,
        onExpire: async () => {
            if (!isExpired && assessmentExam) {
                setIsExpired(true);
                setTimerText('Expired');
                setShowDialog(true);
                await SharedService.Check(authContext);
            }
        },
    });

    const assessmentAccess = authContext.getPermissions("assessments");
    if (assessmentAccess.filter((f) => f.access.includes(1)).length === 0) {
        navigate("/");
    }

    const scrollToItem = () => {
        if (scrollRef.current) {
            const parentContainer = scrollRef.current.parentElement;
            const itemRect = scrollRef.current.getBoundingClientRect();
            const parentRect = parentContainer.getBoundingClientRect();

            const isVisible = (
                itemRect.top >= parentRect.top &&
                itemRect.bottom <= parentRect.bottom &&
                itemRect.left >= parentRect.left &&
                itemRect.right <= parentRect.right
            );

            if (!isVisible) {
                scrollRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'nearest'
                });
            }
        }
    };

    const minutesToTime = (minutes) => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
    }

   const loadQuestions = useCallback(async (signal) => { 
    setLoading(true);
    try {
      const data = await PracticalExamService.GetAll(
        state?.id,
        authContext,
        signal
      );

      if (signal?.aborted) return; 

      if (data.isSuccess) {
            // if (data.data.isPracticalExamCompleted) {
            //     navigate(`/assessments/${assessmentName}/review-practical`, { state: { id: state?.id } });
            // }
        setAssessmentExam(data.data);
        setQuestions(data.data.questions);
        if (data.data.questions.length > 0) {
          setSelectedCardId(data.data.questions[0].id);
          setSelectedCard(data.data.questions[0]);
          setSelectedIndex(0);
        }

        const time = new Date();
        time.setSeconds(time.getSeconds() + data.data.duration);
        restart(time);

        setIsExpired(data.data.duration === 0);
        if (data.data.duration === 0) {
          setTimerText('Expired');
        }

        if (data.data.startDateTime === null) {
          await PracticalExamService.Start(state?.id, authContext);
        }
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [restart, authContext, state?.id]);

    useEffect(() => {
        if (isExpired) {
            setTimerText('Expired');
        } else {
            setTimerText(`${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`);
        }
    }, [hours, minutes, seconds, isExpired]);

useEffect(() => {
    const controller = new AbortController(); 
    if (state?.id && !isExpired) {
      loadQuestions(controller.signal);
    }
    return () => controller.abort();
  }, [loadQuestions, authContext, state?.id, isExpired]);

    const changeHandler = (id, answer) => {
        setQuestions((prev) => {
            if (prev && prev.length > 0 && prev.filter(f => f.id === id).length > 0) {
                let question = prev.filter(f => f.id === id);
                question = question[0];
                question.answer = answer;
            }
            return [...prev];
        });
    };

    const onCardClick = (data, i) => {
        let preId = selectedCardId;
        setSelectedCardId(data.id);
        setSelectedCard(data);
        setSelectedIndex(i);
        if (preId === undefined || preId === null || preId === 0) {
            setTimeout(() => {
                scrollToItem();
            }, 10);
        }
    };

    const moveToPrevious = (e) => {
        setSelectedIndex((prev) => {
            if (prev > 0) {
                return prev - 1;
            } else {
                return 0;
            }
        });
    };

    const moveToNext = (e) => {
        setSelectedIndex((prev) => {
            if (prev === questions.length - 1) {
                return prev;
            } else {
                return prev + 1;
            }
        });
    };

    useEffect(() => {
        if (questions && questions.length > 0) {
            setSelectedCardId(questions[selectedIndex].id);
            setSelectedCard(questions[selectedIndex]);
            setTimeout(() => {
                scrollToItem();
            }, 10);
        }
    }, [selectedIndex, questions]);

    useEffect(() => {
        localStorage.setItem("PracticalExamTimer", totalSeconds);
    }, [totalSeconds]);


const onSubmitHandler = async () => {
  if (submitControllerRef.current) {
    submitControllerRef.current.abort();
  }
  const controller = new AbortController();
  submitControllerRef.current = controller;

  setLoading(true);

  try {
    const payload = assessmentExam;
    payload.quizId = state?.id;

    const data = await PracticalExamService.Save(
      payload,
      authContext,
      controller.signal
    );

    if (controller.signal.aborted) return;

    if (data.isSuccess) {
      AlertService.success("Assessment details saved successfully!");
      await SharedService.Check(authContext);
      navigate(`/assessments/${assessmentName}`, {
        state: { id: state?.id },
      });
    } else {
      AlertService.error(data.errorMessage);
    }
  } catch (ex) {
    if (ex.name === "AbortError") return;
    console.log(ex);
  } finally {
    if (!controller.signal.aborted) setLoading(false);
  }
};

// Unmount cleanup
useEffect(() => {
  return () => {
    submitControllerRef.current?.abort();
  };
}, []);

    const onConfirmSubmitHandler = (event) => {
        event.preventDefault();
        setAssessmentExam((prev) => {
            prev.questions = questions;
            return prev;
        });
        setShowDialog(true);
    };

    return (
        <Fragment>
            <div className="container-fluid question-container mt-3">
                <div className="row d-none d-md-flex">
                    <div className="col-md-2 col-sm-4 scroll-no-buttons" style={{ maxHeight: '82vh', overflowY: 'auto' }}>
                        {loading ? (
                            Array.from({ length: 20 }).map((_, idx) => (
                                <div key={idx} >
                                    <div
                                        className="mt-1 text-center">
                                        <div className="pt-1 pb-1" title='Loading'>
                                            <Skeleton width="100%" height="1.5rem" />
                                        </div>
                                    </div>
                                </div>
                            ))) : (questions &&
                                questions.length > 0 &&
                                questions.map((q, i) => (
                                    <div
                                        key={i}
                                        ref={q.id === selectedCardId ? scrollRef : null}
                                    >
                                        <div
                                            className={
                                                q.id === (selectedCardId ?? 0)
                                                    ? "mt-1 text-center cursor-pointer quiz-active-card"
                                                    : "mt-1 text-center cursor-pointer quiz-card"
                                            }
                                            onClick={(e) => {
                                                e.preventDefault();
                                                onCardClick(q, i);
                                            }}
                                        >
                                            <div className="pt-1 pb-1 cursor-pointer" title={q.question}>
                                                Question {i + 1}
                                            </div>
                                        </div>
                                    </div>
                                )))}
                    </div>
                    <div className="col-md-10 col-sm-8 view mt-2">
                        {loading ? (
                            <div className="d-flex bd-highlight mb-3">
                                <div className='text-primary mt-2'>
                                    <Skeleton width="6rem" height="3.5rem" />
                                </div>
                                <div className="p-2 mt-auto mb-auto" style={{ color: '#104E66', width: '30%' }}>
                                    <div className="title">
                                        <Skeleton width="80%" height="1.5rem" />
                                    </div>
                                    <div className="title pt-1"><Skeleton width="85%" height="1.5rem" /></div>
                                </div>

                                <div className="ms-auto" style={{ width: '40%' }}>
                                    <div className="d-flex flex-column align-items-end" style={{ width: '100%' }}>
                                        <div className="fw-light pt-1" title='Questions'>
                                            <Skeleton width="10rem" height="1.5rem" />
                                        </div>
                                        <div className="fw-light pt-1" title='Duration'>
                                            <Skeleton width="12rem" height="1.5rem" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (<div className="d-flex bd-highlight d-flex bd-highlight bd-highlight flex-wrap flex-md-nowrap align-items-start mb-4 gap-3 quiz-header-responsive">
                            <div className="p-4  mt-auto bg-white" style={{ borderColor: '#FF9900', color: '#FF9900', borderStyle: 'solid', borderWidth: '2px', borderRadius: '5px' }}>
                                {timerText}
                            </div>
                            <div className="p-2 mt-auto mb-auto" style={{ color: isDark ? "#ffffff" : "#104E66" }}>
                                <div className="title">Assessment Name : {assessmentExam && assessmentExam.assessmentName}</div>
                                <div className="title pt-2">Course : {assessmentExam && assessmentExam.courseName}</div>
                            </div>

                            <div className="ms-auto">
                                <div className="d-flex flex-column align-items-end">
                                    <div className="fw-light pt-2" title='Questions'>
                                        <img
                                            src={QuestionMarkIcon}
                                            alt="Questions"
                                            className="me-1 mb-1"
                                        /> Questions : {assessmentExam && assessmentExam.totalQuestions}
                                    </div>
                                    <div className="fw-light pt-2" title='Duration'>
                                        <img
                                            src={ChronometerIcon}
                                            alt="Duration"
                                            className="mb-1 me-1"
                                        /> Duration : {minutesToTime(assessmentExam && assessmentExam.practicalDuration)}
                                    </div>
                                </div>
                            </div>
                        </div>)}
                        {loading ? (
                            <div>
                                <div className="d-flex justify-content-between mb-4 ps-3 pe-3">
                                    <Skeleton width="100%" height="2px" />
                                </div>
                                <div className="d-flex ps-3 pe-3 mb-3">
                                    <Skeleton width="12rem" height="1.5rem" />
                                </div>
                                <div className="d-flex justify-content-between mb-4 ps-3 pe-3">
                                    <Skeleton width="100%" height="7rem" />
                                </div>
                                <div className="d-flex justify-content-between mb-4 ps-3 pe-3">
                                    <Skeleton width="100%" height="2px" />
                                </div>
                                <div className="d-flex ps-3 pe-3 mb-3">
                                    <Skeleton width="12rem" height="1.5rem" />
                                </div>
                                <div className="d-flex justify-content-between mb-4 ps-3 pe-3">
                                    <Skeleton width="100%" height="3rem" />
                                </div>
                                <div className="d-flex justify-content-between mb-4 ps-3 pe-3">
                                    <Skeleton width="100%" height="2px" />
                                </div>
                                <div className="d-flex ps-3 pe-3 mb-3">
                                    <Skeleton width="12rem" height="1.5rem" />
                                </div>
                                <div className="d-flex justify-content-between mb-4 ps-3 pe-3">
                                    <Skeleton width="100%" height="10rem" />
                                </div>
                                <div className="d-flex justify-content-between mb-4 ps-3 pe-3">
                                    <Skeleton width="100%" height="2px" />
                                </div>
                                <div className="d-flex justify-content-between ps-3 pe-3 mb-3">
                                    <Skeleton width="12rem" height="2rem" />
                                    <Skeleton width="12rem" height="2rem" />
                                </div>
                            </div>
                        ) : (
                            questions && questions.length > 0 && <PracticalExamQuestion
                                isExpired={isExpired}
                                input={selectedCard}
                                changeHandler={changeHandler}
                            ></PracticalExamQuestion>
                        )}
                        {!loading && questions && questions.length > 0 && (
                            <div className="d-flex pt-3 ps-2">
                                <div>
                                    {selectedIndex > 0 && (
                                        <button

                                            className="p-0 btn btn-outline"
                                            onClick={moveToPrevious}
                                        >
                                            <div className="d-flex">
                                                <img
                                                    src={isDark ? WhitePreviousIcon : PreviousIcon}
                                                    alt="Previous"
                                                    style={{ width: '40px', marginLeft: '-3px' }}
                                                />
                                                <div className="me-4 mt-2 ms-3">Previous</div>
                                            </div>
                                        </button>
                                    )}
                                </div>
                                <div className="ms-auto mb-5 pe-2">
                                    {selectedIndex < questions.length - 1 && (
                                        <button

                                            className="p-0 btn btn-outline"
                                            onClick={moveToNext}
                                        >
                                            <div className="d-flex m-auto">
                                                <div className="me-4 mt-2 ms-3">Next</div>
                                                <img
                                                    src={isDark ? WhiteNextIcon : NextIcon}
                                                    style={{ width: '40px', marginRight: '-3px' }}
                                                    alt="Previous"
                                                />
                                            </div>
                                        </button>
                                    )}

                                    {selectedIndex === questions.length - 1 && (
                                        <Button
                                            variant='primary'
                                            className="float-right mr-3 submit-button px-md-5 py-2"
                                            onClick={onConfirmSubmitHandler}
                                        >
                                            Submit
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <ConfirmDialogBox
                visible={showDialog}
                onHide={() => setShowDialog(false)}
                onConfirm={() => {
                    setShowDialog(false);
                    onSubmitHandler();
                }}
                title={
                    <>
                        Are you sure you want to submit the <strong>assessment</strong>?
                        <br />
                        <small className="text-muted">
                            Once submitted, you cannot make any changes.
                        </small>
                    </>
                }
                confirmLabel="Submit"
                cancelLabel="Cancel"
            />
        </Fragment >
    );
};

export default PracticalExam;
